// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Explorer timeline + flight/explorer/compare/trend chart canvases:
// - explorerFindingChip / explorerMarkerButton / explorerConditionBandButton:
//   pill, marker, and condition-band HTML for the playback timeline.
// - buildExplorerTimelineMarkers: cached projection of detection windows
//   onto timeline ratios, separated into finding pins and condition bands.
// - drawExplorerTimelineCanvas: phase band + ruler tick rendering on the
//   timeline canvas.
// - renderExplorerTimeline / updateExplorerTimelineCursor / renderExplorerContext:
//   build the full timeline DOM and keep the cursor + annunciations in sync.
// - drawFlightChart / drawExplorerChart / drawChartLanes: primary chart
//   canvas drawing (single + split lanes + detection-window labels).
// - drawCompareChart / drawCylinderCompareChart: side-by-side reference
//   comparison charts on the report page.

import { fuelPressureLimitReferences } from "../charts/fuel-pressure-limits.js";

export function createChartRenderers({
  escapeHtml,
  clamp01,
  colors,
  seriesMeta,
  flightChartPadding,
  explorerChartPadding,
  flightChartLayoutOptions,
  explorerChartLayoutOptions,
  explorerIsNormalFinding,
  findingCylinderReference,
  explorerEgtAnnunciationLabel,
  compactHealthNote,
  explorerAlertTone,
  explorerTimelineWindowGeometry,
  explorerConditionTimelineLayout,
  explorerTimelineTicks,
  explorerTimelineControlIcon,
  playbackDetectionWindows,
  visibleScrubRatio,
  formatElapsedTime,
  nearestPoint,
  explorerPhaseFilteredData,
  phaseFilteredData,
  renderExplorerAnnunciations,
  phaseSegments,
  phaseRailColor,
  phaseLabelOptions,
  syncFlightChartCanvasHeight,
  hasSplitChartLanes,
  syncCanvasDisplaySize,
  syncExplorerChartHeightState,
  plotLayout,
  splitSeriesKeys,
  drawDetectionWindowLabel,
  selectedDetectionWindows,
  specialTestDetectionWindows = () => [],
  drawPhaseLabels,
  updateScrubReadout,
  updateChartOverlays,
  cachedInteractionBounds,
  thermalAxisTypesForKeys,
  thermalAxisRangeForType,
  thermalAxisTypeForKey,
  getExplorerFitYAxis,
  explorerFitThermalRange,
  auxScaleConfig,
  chartLaneRenderPlan,
  drawGrid,
  drawLine,
  drawLaneLabel,
  drawSelectedDetectionWindow,
  compareChartPlan,
  chartBounds,
  formatPointTime,
  drawInlineLegend,
  drawMarker,
  cylinderCompareChartPlan,
  getHealthSummary,
  getFlightData,
  getReferenceData,
  getExplorerTimelineMarkerCache,
  setExplorerTimelineMarkerCache,
  currentEngineDescriptors = () => [],
  engineScopedChannelLabel = () => null,
}) {
  function explorerFindingChip(window) {
    const key = window?.sourceKey ?? window?.key;
    const healthSummary = getHealthSummary();
    const metric = healthSummary?.[key];
    if (key === "mag" && window.responseStatus) {
      return window.responseStatus === "review" && Number.isFinite(metric?.score) && metric.score < 88 ? "Mag check response" : "";
    }
    if (explorerIsNormalFinding(key, metric)) return "";
    if (key === "sensors" && window?.channelLabel) return `${window.channelLabel} signal review`;
    if (key === "divergence") return "Cylinder split";
    if (key === "descentEgtDropout") return "Descent EGT dropout";
    if (key === "inductionLeak") return "Induction leak";
    if (key === "coolingRate") {
      const rate = Number.isFinite(window?.value) ? Math.round(window.value) : null;
      const channel = window?.channelLabel
        || (Number.isFinite(window?.sourceCylinder) ? findingCylinderReference(window.sourceCylinder).label : "")
        || "CHT";
      return rate !== null ? `${channel} ${rate} F/min` : `${channel} cooling`;
    }
    if (key === "egt") {
      const egtLabel = explorerEgtAnnunciationLabel(metric);
      if (egtLabel) return egtLabel;
    }
    const noteChip = compactHealthNote(key, metric);
    if (noteChip && !/^(need|no )/i.test(noteChip)) return noteChip;
    const base = {
      charging: "Start charge",
      cht: "CHT peak",
      chtRhythm: "CHT rhythm",
      combustion: "CHT/EGT pairing",
      carbIce: "Carb ice",
      cooling: "Cooling",
      coolingRate: "Cooling rate",
      egt: "EGT spread",
      electrical: "Bus voltage",
      inductionLeak: "Induction leak",
      mag: "Mag check",
      sensors: "Sensor signal",
      valve: "EGT rhythm",
    }[key] ?? window?.sourceLabel ?? "Finding";
    const label = String(window?.label ?? "");
    const sourceCylinder = label.match(/\bcyl(?:inder)?\s*#?\s*(\d+)/i)?.[1]
      ?? label.match(/\b(?:EGT|CHT)\s*#?\s*(\d+)\b/i)?.[1];
    const cylinder = sourceCylinder
      ? findingCylinderReference(Number(sourceCylinder)).label
      : "";
    return cylinder ? `${base} ${cylinder}` : base;
  }

  function explorerMarkerButton(marker) {
    const left = clamp01(marker.ratio ?? 0) * 100;
    const toneClass = marker.tone ? ` is-${marker.tone}` : "";
    return `<button class="playback-marker is-${marker.type}${toneClass}" data-explorer-marker-ratio="${left / 100}" data-explorer-marker-time="${marker.time ?? ""}" aria-label="${escapeHtml(marker.label)}" title="${escapeHtml(marker.label)}" style="left:${left.toFixed(2)}%" type="button"></button>`;
  }

  function explorerConditionBandButton(marker) {
    const start = clamp01(marker.ratioStart ?? marker.ratio ?? 0) * 100;
    const end = clamp01(marker.ratioEnd ?? marker.ratioStart ?? marker.ratio ?? 0) * 100;
    const left = Math.min(start, end);
    const width = Math.max(0.8, Math.abs(end - start));
    const top = 18 + (marker.conditionLane ?? 0) * 18;
    const toneClass = marker.tone ? ` is-${marker.tone}` : "";
    const keyClass = marker.key ? ` is-${marker.key}` : "";
    const labelHidden = width < 7;
    const labelClass = labelHidden ? " is-label-hidden" : "";
    const label = marker.shortLabel ?? marker.label;
    const content = labelHidden ? "" : `<span>${escapeHtml(label)}</span>`;
    return `<button class="playback-condition-band${keyClass}${toneClass}${labelClass}" data-explorer-marker-ratio="${left / 100}" data-explorer-marker-time="${marker.time ?? ""}" aria-label="${escapeHtml(marker.label)}" title="${escapeHtml(marker.label)}" style="left:${left.toFixed(2)}%; width:${width.toFixed(2)}%; top:${top}px" type="button">${content}</button>`;
  }

  function explorerTimelineLabelControls(markers) {
    const disabled = markers.some((marker) => marker.type === "finding" && Number.isFinite(marker.time)) ? "" : " disabled";
    return `
      <span class="playback-finding-navigation explorer-timeline-label-navigation" role="group" aria-label="Finding marker navigation">
        <button class="playback-finding-nav-button explorer-timeline-label-button" data-explorer-timeline-step="-1" type="button" aria-label="Previous finding marker" title="Previous finding marker"${disabled}>${explorerTimelineControlIcon("previous")}</button>
        <button class="playback-finding-nav-button explorer-timeline-label-button" data-explorer-timeline-step="1" type="button" aria-label="Next finding marker" title="Next finding marker"${disabled}>${explorerTimelineControlIcon("next")}</button>
      </span>
    `;
  }

  function buildExplorerTimelineMarkers(data) {
    const healthSummary = getHealthSummary();
    const cache = getExplorerTimelineMarkerCache();
    if (cache.data === data && cache.summary === healthSummary) {
      return cache.markers;
    }
    const markers = [];
    playbackDetectionWindows().forEach((window) => {
      const geometry = explorerTimelineWindowGeometry(data, window);
      if (!geometry) return;
      const key = window.sourceKey ?? window.key;
      const metric = healthSummary?.[key];
      if (window.kind === "condition") {
        markers.push({
          type: "condition",
          time: window.startTime,
          ratioStart: geometry.leftRatio,
          ratioEnd: Math.max(geometry.leftRatio + 0.004, geometry.rightRatio),
          label: window.label,
          shortLabel: window.shortLabel ?? explorerFindingChip(window),
          key,
          channelKey: window.channelKey,
          tone: explorerAlertTone(key, metric?.score),
        });
        return;
      }
      markers.push({
        type: "finding",
        time: window.startTime,
        ratio: geometry.leftRatio,
        score: Number.isFinite(metric?.score) ? metric.score : null,
        label: window.label,
        key,
      });
    });
    const sorted = markers.sort((a, b) => (a.ratioStart ?? a.ratio ?? 0) - (b.ratioStart ?? b.ratio ?? 0));
    setExplorerTimelineMarkerCache({ data, summary: healthSummary, markers: sorted });
    return sorted;
  }

  function drawExplorerTimelineCanvas(timeline, data, markers, ticks) {
    const body = timeline?.querySelector(".playback-timeline-body");
    const canvas = timeline?.querySelector("[data-explorer-timeline-canvas]");
    if (!body || !canvas || !data.length) return;
    const rect = body.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const targetWidth = Math.round(width * dpr);
    const targetHeight = Math.round(height * dpr);
    if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const rowRect = (selector) => {
      const row = body.querySelector(selector);
      return {
        top: row?.offsetTop ?? 0,
        height: row?.offsetHeight ?? 0,
      };
    };
    const phaseRow = rowRect(".playback-phase-row");
    const rulerRow = rowRect(".playback-ruler-row");

    ctx.save();
    ctx.font = "900 9px Inter, sans-serif";
    ctx.textBaseline = "middle";
    phaseSegments(data).forEach((segment) => {
      const left = clamp01(segment.start / 100) * width;
      const right = clamp01(segment.end / 100) * width;
      const bandWidth = Math.max(1, right - left);
      ctx.fillStyle = phaseRailColor(segment.name, 0.46);
      ctx.fillRect(left, phaseRow.top + 3, bandWidth, Math.max(0, phaseRow.height - 6));
      if (bandWidth >= 7) {
        ctx.strokeStyle = "rgba(255,255,255,0.12)";
        ctx.strokeRect(left + 0.5, phaseRow.top + 3.5, Math.max(0, bandWidth - 1), Math.max(0, phaseRow.height - 7));
      }
      if (bandWidth >= 26) {
        const label = phaseLabelOptions(segment.name).find((option) => ctx.measureText(option).width + 12 <= bandWidth);
        if (label) {
          ctx.fillStyle = "rgba(236, 244, 255, 0.84)";
          ctx.textAlign = "center";
          ctx.fillText(label.toUpperCase(), left + bandWidth / 2, phaseRow.top + phaseRow.height / 2 + 0.5);
        }
      }
    });
    ctx.restore();

    ctx.save();
    ctx.strokeStyle = "rgba(139, 178, 217, 0.18)";
    ticks.minor.forEach((tick) => {
      const x = tick.left / 100 * width;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, rulerRow.top);
      ctx.lineTo(x + 0.5, rulerRow.top + 10);
      ctx.stroke();
    });
    ctx.strokeStyle = "rgba(139, 178, 217, 0.28)";
    ctx.fillStyle = "rgba(203, 213, 225, 0.62)";
    ctx.font = "800 10px Inter, sans-serif";
    ctx.textBaseline = "top";
    ticks.major.forEach((tick, index, items) => {
      const x = tick.left / 100 * width;
      ctx.beginPath();
      ctx.moveTo(x + 0.5, rulerRow.top);
      ctx.lineTo(x + 0.5, rulerRow.top + 16);
      ctx.stroke();
      if (index === 0) {
        ctx.textAlign = "left";
        ctx.fillText(tick.label, 0, rulerRow.top + 18);
      } else if (index === items.length - 1) {
        ctx.textAlign = "right";
        ctx.fillText(tick.label, width, rulerRow.top + 18);
      } else {
        ctx.textAlign = "center";
        ctx.fillText(tick.label, x, rulerRow.top + 18);
      }
    });
    ctx.restore();
  }

  function renderExplorerTimeline(data, point, markers = buildExplorerTimelineMarkers(data)) {
    const container = document.querySelector("#explorerTimeline");
    if (!container) return;
    if (!data.length) {
      container.innerHTML = '<p class="muted">No timeline data is available for the current graph filter.</p>';
      return;
    }

    const conditionLayout = explorerConditionTimelineLayout(markers.filter((marker) => marker.type === "condition"));
    const conditionBands = conditionLayout.markers.map(explorerConditionBandButton).join("");
    const findingMarkers = markers.filter((marker) => marker.type === "finding").map(explorerMarkerButton).join("");
    const findingsRowHeight = 20 + Math.max(1, conditionLayout.laneCount) * 18;
    const ticks = explorerTimelineTicks(data);
    const cursorRatio = clamp01(visibleScrubRatio());
    const currentTime = formatElapsedTime(point?.elapsedSeconds ?? data[Math.round(cursorRatio * (data.length - 1))]?.elapsedSeconds ?? 0);

    container.innerHTML = `
      <div class="playback-timeline is-explorer-timeline" style="--playback-handle-left:${(cursorRatio * 100).toFixed(2)}%; --playback-findings-row-height:${findingsRowHeight}px">
        <div class="playback-timeline-frame">
          <div class="playback-timeline-labels">
            <span>Phase</span>
            <span class="playback-findings-label" title="Named engine-health finding windows"><b>Findings</b>${explorerTimelineLabelControls(markers)}</span>
            <span></span>
          </div>
          <div class="playback-timeline-body">
            <canvas class="explorer-timeline-canvas" data-explorer-timeline-canvas aria-hidden="true"></canvas>
            <div class="playback-timeline-row playback-phase-row"></div>
            <div class="playback-timeline-row playback-findings-row">${conditionBands}${findingMarkers}</div>
            <div class="playback-ruler-row"></div>
            <div class="playback-timeline-cursor" aria-hidden="true">
              <span data-timeline-time>${escapeHtml(currentTime)}</span>
            </div>
          </div>
        </div>
      </div>
    `;
    drawExplorerTimelineCanvas(container.querySelector(".playback-timeline"), data, markers, ticks);
  }

  function updateExplorerTimelineCursor(data, point, ratio = visibleScrubRatio()) {
    const timeline = document.querySelector("#explorerTimeline .playback-timeline");
    if (!timeline) return;
    const cursorRatio = clamp01(ratio);
    const cursorLeft = `${(cursorRatio * 100).toFixed(2)}%`;
    const cursor = timeline.querySelector(".playback-timeline-cursor");
    if (cursor) cursor.style.left = cursorLeft;
    const label = timeline.querySelector("[data-timeline-time]");
    if (label) {
      const fallbackPoint = data[Math.round(cursorRatio * Math.max(data.length - 1, 0))];
      const text = formatElapsedTime(point?.elapsedSeconds ?? fallbackPoint?.elapsedSeconds ?? 0);
      if (label.textContent !== text) label.textContent = text;
    }
  }

  function renderExplorerContext(data = explorerPhaseFilteredData(), point = nearestPoint(data, visibleScrubRatio())) {
    const markers = buildExplorerTimelineMarkers(data);
    renderExplorerTimeline(data, point, markers);
    renderExplorerAnnunciations(data, point, markers);
  }

  function explorerReadoutValue(key, point) {
    const value = point?.[key];
    if (!Number.isFinite(value)) return "--";
    const digits = /^(?:fuelFlow|fuelUsed|map|buss|amps|fuelPressure|upperDeckPressure)/i.test(key) ? 1 : 0;
    return Number(value).toFixed(digits);
  }

  function explorerReadoutLabel(key) {
    const cylinder = String(key).match(/^(?:egt|cht)(\d+)$/i)?.[1];
    if (cylinder) return cylinder;
    return String(seriesMeta[key]?.label ?? key)
      .replace(/\s*\([^)]*\)\s*$/g, "")
      .replace(/^Manifold Pressure$/i, "MAP")
      .replace(/^Fuel Flow$/i, "FF")
      .replace(/^Oil Pressure$/i, "Oil P")
      .replace(/^Oil Temperature$/i, "Oil T");
  }

  function explorerCompactChannelLabel(label) {
    return String(label ?? "")
      .replace(/^Left\b/i, "L")
      .replace(/^Right\b/i, "R")
      .replace(/^Front\b/i, "F")
      .replace(/^Rear\b/i, "R")
      .replace(/^Engine\s+/i, "")
      .replace(/\s+Engine\b/gi, "")
      .replace(/\bFuel Flow\b/gi, "FF")
      .replace(/\bFuel Used\b/gi, "Fuel")
      .replace(/\bOil Pressure\b/gi, "Oil P")
      .replace(/\bOil Temp(?:erature)?\b/gi, "Oil T")
      .replace(/\bInduction Temp(?:erature)?\b/gi, "Ind T")
      .replace(/\bCompressor (?:Discharge )?Temp(?:erature)?\b/gi, "Comp T")
      .replace(/\bUpper Deck\b/gi, "UDP")
      .replace(/\bFuel Pressure\b/gi, "Fuel P")
      .replace(/\bManifold Pressure\b/gi, "MAP")
      .replace(/\s+\([^)]*\)\s*$/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function explorerAverageReadoutLabel(key, data) {
    const engines = currentEngineDescriptors(data);
    if (engines.length <= 1) return null;
    if (engineScopedChannelLabel(key, engines, { style: "narrative" })) return null;
    const meta = seriesMeta[key];
    if (!meta?.auxGroup && !["egt", "cht"].includes(key)) return null;
    return `Avg ${explorerCompactChannelLabel(explorerReadoutLabel(key))}`;
  }

  function explorerSecondaryReadoutLabel(key, data) {
    // Thermal channels relocated to the secondary pane would otherwise label
    // as a bare cylinder number ("3") via explorerReadoutLabel.
    const thermal = String(key).match(/^(egt|cht)(\d+)?$/i);
    if (thermal) return `${thermal[1].toUpperCase()}${thermal[2] ? ` ${thermal[2]}` : ""}`;
    const scoped = engineScopedChannelLabel(key, currentEngineDescriptors(data), { style: "narrative" });
    if (scoped) return explorerCompactChannelLabel(scoped);
    return explorerAverageReadoutLabel(key, data) ?? explorerCompactChannelLabel(explorerReadoutLabel(key));
  }

  function explorerReadoutGroupRows(keys, point, limit, labelForKey = explorerReadoutLabel) {
    return keys
      .filter((key) => Number.isFinite(point?.[key]))
      .slice(0, limit)
      .map((key) => ({
        key,
        color: seriesMeta[key]?.color ?? colors.text,
        label: labelForKey(key),
        value: explorerReadoutValue(key, point),
      }));
  }

  function explorerReadoutEngineTitle(engine, family, engines) {
    const metric = family.toUpperCase();
    if ((engines?.length ?? 0) <= 1) return `${metric} (F)`;
    const label = String(engine?.label ?? engine?.shortLabel ?? "Engine")
      .replace(/\s+Engine$/i, "")
      .trim()
      .toUpperCase();
    return `${label || String(engine?.shortLabel ?? "").toUpperCase()} ${metric}`;
  }

  function explorerReadoutEngineRows(engine, family, activeKeys, point) {
    const keyField = `${family}Key`;
    const active = new Set(activeKeys);
    const keys = [];
    const labels = new Map();
    (engine?.cylinders ?? []).forEach((cylinder) => {
      const key = cylinder[keyField];
      if (!key || !active.has(key)) return;
      keys.push(key);
      labels.set(key, String(cylinder.number ?? explorerReadoutLabel(key)));
    });
    return explorerReadoutGroupRows(keys, point, 16, (key) => labels.get(key) ?? explorerReadoutLabel(key));
  }

  function explorerThermalReadoutGroups(data, point, activeKeys) {
    const engines = currentEngineDescriptors(data);
    const groups = [];
    if (engines.length) {
      ["egt", "cht"].forEach((family) => {
        engines.forEach((engine) => {
          const rows = explorerReadoutEngineRows(engine, family, activeKeys, point);
          if (rows.length) groups.push({
            title: explorerReadoutEngineTitle(engine, family, engines),
            rows,
          });
        });
      });
    }
    if (groups.length) return groups;
    return [
      { title: "EGT (F)", rows: explorerReadoutGroupRows(activeKeys.filter((key) => /^egt\d*$/i.test(key)), point, 16) },
      { title: "CHT (F)", rows: explorerReadoutGroupRows(activeKeys.filter((key) => /^cht\d*$/i.test(key)), point, 16) },
    ].filter((group) => group.rows.length);
  }

  function drawExplorerReadoutGroup(ctx, { title, rows, x, y, width, maxBottom, rowStep = 13, titleStep = 15, bottomGap = 8 }) {
    if (!rows.length || y >= maxBottom - 16) return y;
    const titleFontSize = titleStep <= 11 ? 10 : 11;
    const rowFontSize = rowStep <= 10 ? 10 : 11;
    ctx.save();
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "rgba(220, 231, 243, 0.92)";
    ctx.font = `800 ${titleFontSize}px Inter, sans-serif`;
    ctx.fillText(title, x, y);
    y += titleStep;
    ctx.font = `800 ${rowFontSize}px Inter, sans-serif`;
    rows.forEach((row) => {
      if (y > maxBottom - 5) return;
      ctx.strokeStyle = row.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(x, y - 4);
      ctx.lineTo(x + 16, y - 4);
      ctx.stroke();
      ctx.fillStyle = "rgba(226, 237, 249, 0.88)";
      ctx.textAlign = "left";
      ctx.save();
      ctx.beginPath();
      ctx.rect(x + 24, y - rowStep + 1, Math.max(12, width - 56), rowStep + 3);
      ctx.clip();
      ctx.fillText(row.label, x + 24, y);
      ctx.restore();
      ctx.textAlign = "right";
      ctx.fillText(row.value, x + width, y);
      y += rowStep;
    });
    ctx.restore();
    return y + bottomGap;
  }

  function explorerThermalReadoutSpacing(groups, top, bottom) {
    const totalRows = groups.reduce((total, group) => total + group.rows.length, 0);
    const available = Math.max(0, bottom - top);
    const roomyHeight = 21 + groups.length * (15 + 8) + totalRows * 13;
    if (roomyHeight <= available) return { titleGap: 21, rowStep: 13, groupTitleStep: 15, groupGap: 8 };
    const compactHeight = 17 + groups.length * (12 + 3) + totalRows * 11;
    if (compactHeight <= available) return { titleGap: 17, rowStep: 11, groupTitleStep: 12, groupGap: 3 };
    return { titleGap: 15, rowStep: 10, groupTitleStep: 11, groupGap: 2 };
  }

  function explorerAuxReadoutSpacing(rowCount, top, bottom) {
    const available = Math.max(0, bottom - top);
    if (15 + rowCount * 13 + 8 <= available) return { rowStep: 13, titleStep: 15, bottomGap: 8 };
    if (12 + rowCount * 11 + 3 <= available) return { rowStep: 11, titleStep: 12, bottomGap: 3 };
    if (11 + rowCount * 10 + 2 <= available) return { rowStep: 10, titleStep: 11, bottomGap: 2 };
    return { rowStep: 9, titleStep: 10, bottomGap: 1 };
  }

  let explorerReadoutHover = false;
  let explorerChartSnapshot = null;
  // Panel rects from the previous readout repaint. Panel height is
  // point-dependent (rows with no value at the cursor are dropped), so a
  // scrub repaint must also restore where the panels USED to be — restoring
  // only the new, shorter panels would leave the old bottom strip (fill,
  // border, stale text) frozen on the canvas until the next full draw.
  let explorerLastReadoutPanels = [];

  function setExplorerReadoutHover(value) {
    explorerReadoutHover = Boolean(value);
  }

  // Snapshot of the explorer canvas as of the last full draw, taken before
  // the readout panels land. Scrub moves repaint only the readout, so the
  // translucent panels restore the chart pixels beneath them from this
  // snapshot before each repaint instead of stacking fills frame on frame.
  function captureExplorerChartSnapshot(canvas) {
    if (typeof document.createElement !== "function") return;
    if (!explorerChartSnapshot || explorerChartSnapshot.width !== canvas.width || explorerChartSnapshot.height !== canvas.height) {
      explorerChartSnapshot = document.createElement("canvas");
      explorerChartSnapshot.width = canvas.width;
      explorerChartSnapshot.height = canvas.height;
    }
    const snapshotCtx = explorerChartSnapshot.getContext("2d");
    snapshotCtx.clearRect(0, 0, explorerChartSnapshot.width, explorerChartSnapshot.height);
    snapshotCtx.drawImage(canvas, 0, 0);
  }

  function explorerSnapshotMatches(canvas) {
    return Boolean(explorerChartSnapshot)
      && explorerChartSnapshot.width === canvas.width
      && explorerChartSnapshot.height === canvas.height;
  }

  function restoreExplorerChartRegion(ctx, { left, top, width, bottom }) {
    const canvas = ctx.canvas;
    if (!explorerSnapshotMatches(canvas)) return;
    const pad = 2;
    const x = Math.max(0, left - pad);
    const y = Math.max(0, top - pad);
    const w = Math.min(canvas.width - x, width + pad * 2);
    const h = Math.min(canvas.height - y, bottom - top + pad * 2);
    if (w <= 0 || h <= 0) return;
    // The chart background is transparent (the page supplies it), so clear
    // first: drawImage alone would composite over the previous panel pixels.
    ctx.clearRect(x, y, w, h);
    ctx.drawImage(explorerChartSnapshot, x, y, w, h, x, y, w, h);
  }

  // Mirrors drawExplorerReadoutGroup's y advancement (including its clipping
  // rules) without drawing, so the opaque panel background can be sized
  // before any text lands on the canvas.
  function readoutGroupEndY(rowCount, y, maxBottom, rowStep, titleStep, bottomGap) {
    if (!rowCount || y >= maxBottom - 16) return y;
    y += titleStep;
    for (let i = 0; i < rowCount; i += 1) {
      if (y <= maxBottom - 5) y += rowStep;
    }
    return y + bottomGap;
  }

  function drawExplorerReadoutPanel(ctx, { left, top, width, bottom }) {
    if (bottom - top < 18) return;
    ctx.save();
    // Translucent so the traces stay visible behind the panel; fall back to
    // opaque when no snapshot exists to restore from, because repeated scrub
    // repaints would otherwise stack the fill and smear the text.
    ctx.fillStyle = explorerSnapshotMatches(ctx.canvas) ? "rgba(10, 18, 30, 0.78)" : "rgb(10, 18, 30)";
    ctx.strokeStyle = "rgba(139, 178, 217, 0.32)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (typeof ctx.roundRect === "function") ctx.roundRect(left, top, width, bottom - top, 8);
    else ctx.rect(left, top, width, bottom - top);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // The channel readout is a cursor instrument, not a legend (the Data Traces
  // chips carry channel identity): it renders as a compact panel pinned to
  // the top-left of each lane while the pointer is over the graph (toggled
  // via setExplorerReadoutHover) and hides otherwise, so the plot keeps the
  // full canvas width. The panels are translucent; because scrub moves
  // repaint only the readout, each repaint first restores the chart pixels
  // beneath the panel from the last full-draw snapshot.
  function drawExplorerChannelReadout(ctx, layout, point, data = []) {
    if (!explorerReadoutHover || !point || !layout?.full) {
      // The readout may be hiding mid-scrub: erase the previous panels from
      // the snapshot instead of leaving them frozen with stale values.
      explorerLastReadoutPanels.forEach((panel) => restoreExplorerChartRegion(ctx, panel));
      explorerLastReadoutPanels = [];
      return;
    }
    const readoutWidth = 118;
    const panelPad = 10;
    const panelWidth = readoutWidth + panelPad * 2;
    const panelLeft = layout.full.left + 12;
    const x = panelLeft + panelPad;

    const thermalGroups = explorerThermalReadoutGroups(data, point, layout.split.primary);
    // Guest (non-thermal) channels living in the primary pane still read out
    // alongside the secondary channels.
    const channelReadoutKeys = [
      ...layout.split.primary.filter((key) => !thermalAxisTypeForKey(key)),
      ...layout.split.aux,
    ];
    const auxRows = explorerReadoutGroupRows(channelReadoutKeys, point, channelReadoutKeys.length, (key) => explorerSecondaryReadoutLabel(key, data));
    let primaryY = (layout.primary ?? layout.full).top + 26;

    // Measure pass: replicate the draw flow below to size the panels.
    const primaryBounds = layout.primary ?? layout.full;
    const measureSpacing = explorerThermalReadoutSpacing(thermalGroups, primaryY, primaryBounds.bottom);
    let measureY = primaryY;
    if (thermalGroups.length) {
      measureY += measureSpacing.titleGap;
      thermalGroups.forEach((group) => {
        measureY = readoutGroupEndY(group.rows.length, measureY, primaryBounds.bottom, measureSpacing.rowStep, measureSpacing.groupTitleStep, measureSpacing.groupGap);
      });
    }
    if (!layout.aux && auxRows.length) {
      const continuationSpacing = explorerAuxReadoutSpacing(auxRows.length, measureY, layout.full.bottom);
      measureY = readoutGroupEndY(auxRows.length, measureY, layout.full.bottom, continuationSpacing.rowStep, continuationSpacing.titleStep, continuationSpacing.bottomGap);
    }
    const panels = [];
    if (measureY > primaryY) {
      panels.push({
        left: panelLeft,
        top: primaryY - 16,
        width: panelWidth,
        bottom: Math.min(measureY, primaryBounds.bottom) + 2,
      });
    }
    if (layout.aux && auxRows.length) {
      const auxPanelTop = layout.aux.top + 4;
      const auxPanelSpacing = explorerAuxReadoutSpacing(auxRows.length, auxPanelTop, layout.aux.bottom);
      const auxMeasureY = readoutGroupEndY(auxRows.length, auxPanelTop, layout.aux.bottom, auxPanelSpacing.rowStep, auxPanelSpacing.titleStep, auxPanelSpacing.bottomGap);
      panels.push({
        left: panelLeft,
        top: auxPanelTop - 14,
        width: panelWidth,
        bottom: Math.min(auxMeasureY, layout.aux.bottom) + 2,
      });
    }
    explorerLastReadoutPanels.forEach((panel) => restoreExplorerChartRegion(ctx, panel));
    panels.forEach((panel) => restoreExplorerChartRegion(ctx, panel));
    panels.forEach((panel) => drawExplorerReadoutPanel(ctx, panel));
    explorerLastReadoutPanels = panels;

    if (thermalGroups.length) {
      const primaryBounds = layout.primary ?? layout.full;
      const spacing = explorerThermalReadoutSpacing(thermalGroups, primaryY, primaryBounds.bottom);
      ctx.save();
      ctx.fillStyle = "rgba(220, 231, 243, 0.95)";
      ctx.font = "900 13px Inter, sans-serif";
      ctx.fillText("Temperatures", x, primaryY);
      ctx.restore();
      primaryY += spacing.titleGap;
      thermalGroups.forEach((group) => {
        primaryY = drawExplorerReadoutGroup(ctx, {
          ...group,
          x,
          y: primaryY,
          width: readoutWidth,
          maxBottom: primaryBounds.bottom,
          rowStep: spacing.rowStep,
          titleStep: spacing.groupTitleStep,
          bottomGap: spacing.groupGap,
        });
      });
    }
    if (auxRows.length) {
      const auxBounds = layout.aux ?? layout.full;
      const auxY = layout.aux ? auxBounds.top + 4 : primaryY;
      const auxSpacing = explorerAuxReadoutSpacing(auxRows.length, auxY, auxBounds.bottom);
      drawExplorerReadoutGroup(ctx, {
        title: layout.aux ? "Secondary" : "Channels",
        rows: auxRows,
        x,
        y: auxY,
        width: readoutWidth,
        maxBottom: auxBounds.bottom,
        ...auxSpacing,
      });
    }
  }

  function updateExplorerChannelReadout(data = explorerPhaseFilteredData(), point = nearestPoint(data, visibleScrubRatio())) {
    const canvas = document.querySelector("#explorerChart");
    if (!canvas || !point) return;
    const ctx = canvas.getContext("2d");
    const layout = plotLayout(canvas, explorerChartPadding, splitSeriesKeys(data), explorerChartLayoutOptions);
    drawExplorerChannelReadout(ctx, layout, point, data);
  }

  function drawFlightChart() {
    const canvas = document.querySelector("#flightChart");
    if (!canvas) return;
    const data = phaseFilteredData();
    syncFlightChartCanvasHeight(canvas, hasSplitChartLanes(data), flightChartPadding, flightChartLayoutOptions);
    syncCanvasDisplaySize(canvas);
    const ctx = canvas.getContext("2d");
    const markerRatio = visibleScrubRatio();
    const point = nearestPoint(data, markerRatio);
    const layout = plotLayout(canvas, flightChartPadding, splitSeriesKeys(data), flightChartLayoutOptions);
    const reservedRects = drawChartLanes(ctx, canvas, data, layout, 2.3);
    drawDetectionWindowLabel(ctx, data, layout.full, selectedDetectionWindows(), reservedRects);
    drawPhaseLabels(ctx, layout.phaseRail, data);
    if (point) updateScrubReadout(point, { scope: "flight" });
    updateChartOverlays(canvas, cachedInteractionBounds(canvas, flightChartPadding, flightChartLayoutOptions), markerRatio, point);
  }

  function drawExplorerChart() {
    const canvas = document.querySelector("#explorerChart");
    if (!canvas) return;
    const data = explorerPhaseFilteredData();
    syncExplorerChartHeightState(data);
    syncCanvasDisplaySize(canvas);
    const ctx = canvas.getContext("2d");
    const markerRatio = visibleScrubRatio();
    const point = nearestPoint(data, markerRatio);
    const layout = plotLayout(canvas, explorerChartPadding, splitSeriesKeys(data), explorerChartLayoutOptions);
    const reservedRects = drawChartLanes(ctx, canvas, data, layout, 2.8);
    drawDetectionWindowLabel(ctx, data, layout.full, selectedDetectionWindows(), reservedRects);
    drawDetectionWindowLabel(ctx, data, layout.full, specialTestDetectionWindows(), reservedRects);
    captureExplorerChartSnapshot(canvas);
    drawExplorerChannelReadout(ctx, layout, point, data);
    if (point) updateScrubReadout(point, { scope: "explorer" });
    updateChartOverlays(canvas, cachedInteractionBounds(canvas, explorerChartPadding, explorerChartLayoutOptions), markerRatio, point);
    renderExplorerContext(data, point);
  }

  function drawChartLanes(ctx, canvas, data, layout, lineWidth) {
    const { primary, aux, split } = layout;
    const labelRects = [];
    if (!data.length || (!split.primary.length && !split.aux.length)) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return labelRects;
    }

    const drawPlotBackgrounds = () => {
      drawSelectedDetectionWindow(ctx, data, layout.full, selectedDetectionWindows());
      if (canvas.id === "explorerChart") {
        drawSelectedDetectionWindow(ctx, data, layout.full, specialTestDetectionWindows());
      }
    };

    const pressureLimits = fuelPressureLimitReferences(getHealthSummary()?.fuelPressure?.details?.engines, [...split.primary, ...split.aux]);
    const scaleData = pressureLimits.length ? [...data, ...pressureLimits.map(limit => ({ [limit.key]: limit.value }))] : data;
    const fitY = canvas.id === "explorerChart" && getExplorerFitYAxis();
    let primaryAxis = null;
    let rightAxis = null;
    let primaryGuestScale = null;
    const primaryAxisByKey = {};
    if (primary) {
      const thermalTypes = thermalAxisTypesForKeys(split.primary);
      const flightData = getFlightData();
      const axisData = flightData.length ? flightData : data;
      const hasEgt = thermalTypes.includes("egt");
      const hasCht = thermalTypes.includes("cht");
      const egtRange = hasEgt
        ? (fitY ? explorerFitThermalRange("egt", data) : thermalAxisRangeForType("egt", axisData))
        : null;
      const chtRange = hasCht
        ? (fitY ? explorerFitThermalRange("cht", data) : thermalAxisRangeForType("cht", axisData))
        : null;
      primaryAxis = hasEgt ? egtRange : chtRange;
      rightAxis = hasEgt && hasCht ? chtRange : null;
      split.primary.forEach((key) => {
        const type = thermalAxisTypeForKey(key);
        if (!type) return;
        const range = type === "egt" ? egtRange : chtRange;
        primaryAxisByKey[key] = range ?? primaryAxis;
      });
      // Non-thermal channels dragged into the primary pane keep their own
      // scale (shared when compatible) instead of flatlining on the EGT axis.
      const guestKeys = split.primary.filter((key) => !thermalAxisTypeForKey(key));
      if (guestKeys.length) {
        primaryGuestScale = auxScaleConfig(scaleData, guestKeys, { fit: fitY });
        if (primaryGuestScale.independent) {
          primaryGuestScale = {
            ...primaryGuestScale,
            label: "Guest channels (own scales)",
            labels: ["High", "", "", "", "Low"],
          };
        }
      }
    }

    const auxScale = aux ? auxScaleConfig(scaleData, split.aux, { fit: fitY }) : null;
    const plan = chartLaneRenderPlan({
      data,
      layout,
      split,
      seriesMetaByKey: seriesMeta,
      primaryAxis,
      rightAxis,
      primaryAxisByKey,
      primaryGuestScale,
      auxScale,
      lineWidth,
    });
    if (plan.empty) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return labelRects;
    }

    for (const lane of plan.lanes) {
      for (const line of lane.lines) {
        if (line.options.independentScale && pressureLimits.some(limit => limit.key === line.key)) {
          const range = auxScaleConfig(scaleData, [line.key], { fit: true }).range;
          line.options = { ...line.options, min: range.min, max: range.max };
        }
      }
    }

    // Publish what this render actually drew, so a user-drawn shape can be
    // resolved to VALUES and not just to a time range. The chart is multi-lane
    // and dual-axis (EGT left, CHT right, aux below), so a normalized y means
    // nothing without knowing which lane it fell in and what that lane's range
    // was at draw time.
    //
    // It rides on the canvas element rather than app state on purpose: it
    // describes this canvas's current frame, both the writer and the reader
    // already hold the canvas, and annotation y is captured as a fraction of
    // this same element's height. Threading it through app.ts would add a
    // module-list registration to three harnesses to say something only these
    // two places need.
    if (canvas.id === "explorerChart" && canvas.height > 0) {
      canvas.explorerAxisMap = {
        lanes: plan.lanes.map((lane) => ({
          topFraction: lane.bounds.top / canvas.height,
          bottomFraction: lane.bounds.bottom / canvas.height,
          traces: lane.lines
            .map((line) => ({
              key: line.key,
              min: Number(line.options?.min),
              max: Number(line.options?.max),
            }))
            .filter((trace) => Number.isFinite(trace.min) && Number.isFinite(trace.max) && trace.max > trace.min),
        })).filter((lane) => lane.traces.length
          && Number.isFinite(lane.topFraction)
          && Number.isFinite(lane.bottomFraction)
          && lane.bottomFraction > lane.topFraction),
      };
    }

    plan.lanes.forEach((lane) => {
      const gridOptions = canvas.id === "explorerChart"
        ? { ...lane.grid.options, labelFont: "12px Inter, sans-serif", leftLabelX: Math.max(8, lane.bounds.left - 30) }
        : lane.grid.options;
      drawGrid(ctx, canvas, lane.bounds, lane.grid.labels, gridOptions);
      if (lane.drawBackgrounds) drawPlotBackgrounds();
      lane.lines.forEach((line) => {
        drawLine(ctx, data, line.key, line.meta, lane.bounds, line.options);
        pressureLimits.filter(limit => limit.key === line.key).forEach((limit, index) => {
          const referenceData = data.map(point => ({ ...point, [line.key]: limit.value }));
          drawLine(ctx, referenceData, line.key, line.meta, lane.bounds, { ...line.options, width: 1, dash: [5, 4] });
          const ratio = (limit.value - line.options.min) / (line.options.max - line.options.min);
          if (Number.isFinite(ratio) && ratio >= 0 && ratio <= 1) {
            ctx.save();
            ctx.fillStyle = line.meta.color;
            ctx.font = "11px Inter, sans-serif";
            ctx.textAlign = "right";
            const y = lane.bounds.bottom - ratio * (lane.bounds.bottom - lane.bounds.top);
            ctx.fillText(limit.label, lane.bounds.right - 6, Math.max(lane.bounds.top + 12, Math.min(lane.bounds.bottom - 3, y + (index ? -4 : 12))));
            ctx.restore();
          }
        });
      });
      lane.labels.forEach((item) => {
        labelRects.push(drawLaneLabel(ctx, lane.bounds, item.label, item.options));
      });
    });
    return labelRects.filter(Boolean);
  }

  function drawCompareChart() {
    const canvas = document.querySelector("#compareChart");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const data = getFlightData().slice(38, 77);
    const ref = getReferenceData().slice(38, 77);
    const markerPoint = nearestPoint(data, 0.45);
    const plan = compareChartPlan(data, ref, markerPoint, formatPointTime(markerPoint));
    const hasRightAxis = Boolean(plan.grid.options.rightLabels?.length);
    const bounds = chartBounds(canvas, 56, hasRightAxis ? { rightPadding: 64 } : {});
    drawGrid(ctx, canvas, bounds, plan.grid.labels, plan.grid.options);
    plan.lines.forEach((line) => drawLine(ctx, line.data, line.key, line.meta, bounds, line.options));
    drawInlineLegend(ctx, bounds, plan.legend.labels, plan.legend.colors);
    if (plan.marker) drawMarker(ctx, bounds, plan.marker.ratio, plan.marker.label, plan.marker.lines);
  }

  function drawCylinderCompareChart() {
    const canvas = document.querySelector("#cylinderCompareChart");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const bounds = chartBounds(canvas, 56);
    const data = getFlightData().slice(38, 77);
    const ref = getReferenceData().slice(38, 77);
    const plan = cylinderCompareChartPlan(data, ref);
    drawGrid(ctx, canvas, bounds, plan.grid.labels, plan.grid.options);
    plan.series.forEach((line) => drawLine(ctx, line.data, line.key, line.meta, bounds, line.options));
    drawInlineLegend(ctx, bounds, plan.legend.labels, plan.legend.colors);
  }

  return {
    explorerFindingChip,
    explorerMarkerButton,
    explorerConditionBandButton,
    buildExplorerTimelineMarkers,
    drawExplorerTimelineCanvas,
    renderExplorerTimeline,
    updateExplorerTimelineCursor,
    renderExplorerContext,
    drawFlightChart,
    drawExplorerChart,
    drawChartLanes,
    updateExplorerChannelReadout,
    setExplorerReadoutHover,
    drawCompareChart,
    drawCylinderCompareChart,
  };
}
